'use client'

import { useState, useMemo, useEffect } from 'react'
import { ChevronDown, MapPin } from 'lucide-react'
import { Showtime, Theatre, UserLocation } from '@/types'
import { Skeleton } from '@/components/ui/skeleton'
import { parseShowtimeAttributes, getFormatPriority } from '@/lib/amc-api'
import { formatDistance } from '@/lib/distance'

interface ShowtimesCollapsibleProps {
  showtimes: Showtime[]
  theaterNames: Map<string, string>
  theaters?: Theatre[]
  userLocation?: UserLocation | null
  scheduledShowtimes?: Map<string, any[]>
}

interface GroupedShowtimes {
  [theaterName: string]: {
    theaterId: string
    distance?: number
    formats: {
      [format: string]: {
        [date: string]: Showtime[]
      }
    }
  }
}

const EMPTY_ARRAY: Theatre[] = []
const EMPTY_MAP = new Map<string, any[]>()

export default function ShowtimesCollapsible({ 
  showtimes, 
  theaterNames,
  theaters = EMPTY_ARRAY,
  userLocation,
  scheduledShowtimes = EMPTY_MAP,
}: ShowtimesCollapsibleProps) {
  // Initialize with today's date expanded by default
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({})

  const toggleDatesExpand = (key: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Create theater lookup map with distance info
  const theaterLookup = useMemo(() => {
    const map = new Map<string, Theatre>()
    theaters.forEach(t => {
      const tid = t.theatreId || (t as any).id
      if (tid) {
        map.set(String(tid), t)
      }
    })
    return map
  }, [theaters])

  // Merge scheduled showtimes with current showtimes
  const mergedShowtimes = useMemo(() => {
    const merged = [...showtimes]
    const existingShowIds = new Set(showtimes.map(s => String(s.showId)))

    // Get current movie ID from showtimes
    const currentMovieId = showtimes.length > 0 ? String(showtimes[0].movieId) : null

    // Add scheduled showtimes that aren't already in the current list
    scheduledShowtimes.forEach((theatreShowtimes, theaterId) => {
      theatreShowtimes.forEach((scheduled: any) => {
        // Only merge if it's for the current movie
        if (String(scheduled.movieId) !== currentMovieId) {
          return
        }

        // Check for duplicates using showId (prefer this) or id fallback
        const showId = String(scheduled.showId || scheduled.id)
        
        if (!existingShowIds.has(showId)) {
          // Ensure we have valid dates
          const dateTimeLocal = scheduled.showDateTimeLocal || scheduled.showDateTimeUtc || ''
          
          if (!dateTimeLocal) {
            console.warn('[ShowtimesCollapsible] Skipping showtime with invalid date:', scheduled)
            return
          }

          // Convert scheduled showtime format to match Showtime interface
          const newShowtime: Showtime = {
            showId: showId,
            movieId: String(scheduled.movieId),
            theatreId: String(scheduled.theatreId),
            theatreName: theaterNames.get(String(scheduled.theatreId)) || 'Unknown Theater',
            showDateTimeUtc: scheduled.showDateTimeUtc,
            showDateTimeLocal: dateTimeLocal,
            attributes: scheduled.attributes || [],
            purchaseUrl: scheduled.purchaseUrl || scheduled.ticketingUrl || '',
            movieName: scheduled.movieName || 'Unknown Movie',
            isSoldOut: scheduled.isSoldOut || false,
            isCanceled: scheduled.isCanceled || false,
          }
          
          merged.push(newShowtime)
          existingShowIds.add(showId)
        }
      })
    })

    return merged
  }, [showtimes, scheduledShowtimes, theaterNames])

  // Group showtimes by theater -> format -> date, sorted by distance
  const groupedShowtimes = useMemo(() => {
    const grouped: GroupedShowtimes = {}

    mergedShowtimes.forEach(showtime => {
      const theaterName = theaterNames.get(String(showtime.theatreId)) || showtime.theatreName || 'Unknown Theater'
      const theater = theaterLookup.get(String(showtime.theatreId))
      const parsed = parseShowtimeAttributes(showtime.attributes)
      const formatBase = parsed.format || 'Standard'
      const format = parsed.language ? `${formatBase} - ${parsed.language}` : formatBase
      const dateOnly = showtime.showDateTimeLocal?.split('T')[0] || 'Unknown Date'

      if (!grouped[theaterName]) {
        grouped[theaterName] = {
          theaterId: String(showtime.theatreId),
          distance: theater?.distance,
          formats: {},
        }
      }
      if (!grouped[theaterName].formats[format]) {
        grouped[theaterName].formats[format] = {}
      }
      if (!grouped[theaterName].formats[format][dateOnly]) {
        grouped[theaterName].formats[format][dateOnly] = []
      }

      grouped[theaterName].formats[format][dateOnly].push(showtime)
    })

    return grouped
  }, [mergedShowtimes, theaterNames, theaterLookup])

  // Sort theater names by distance
  const sortedTheaterNames = useMemo(() => {
    return Object.keys(groupedShowtimes).sort((a, b) => {
      const distA = groupedShowtimes[a].distance ?? Infinity
      const distB = groupedShowtimes[b].distance ?? Infinity
      return distA - distB
    })
  }, [groupedShowtimes])

  // Set initial expanded state after component mounts
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const initialExpanded = new Set<string>()
    
    // Expand today's date
    initialExpanded.add(today)
    
    // Expand only the nearest theater by default
    if (sortedTheaterNames.length > 0) {
      initialExpanded.add(sortedTheaterNames[0])
    }
    
    setExpandedGroups(initialExpanded)
  }, [groupedShowtimes, sortedTheaterNames])

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId)
    } else {
      newExpanded.add(groupId)
    }
    setExpandedGroups(newExpanded)
  }

  const formatTime = (dateTimeLocal: string) => {
    try {
      if (!dateTimeLocal) return 'Invalid'
      const date = new Date(dateTimeLocal)
      if (isNaN(date.getTime())) return 'Invalid'
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } catch {
      return 'Invalid'
    }
  }

  const formatDateLabel = (date: string) => {
    try {
      if (!date) return 'Unknown Date'
      
      // If it's just a date string (YYYY-MM-DD), add time
      let dateToUse = date
      if (!date.includes('T')) {
        dateToUse = date + 'T12:00:00'
      }
      
      const d = new Date(dateToUse)
      if (isNaN(d.getTime())) {
        console.warn('[ShowtimesCollapsible] Invalid date:', date)
        return date
      }
      
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      const dateStr = d.toDateString()
      if (dateStr === today.toDateString()) return 'Today'
      if (dateStr === tomorrow.toDateString()) return 'Tomorrow'
      
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    } catch {
      return date
    }
  }

  return (
    <div className="space-y-3">
      {sortedTheaterNames.map(theaterName => {
        const theaterData = groupedShowtimes[theaterName]
        const isExpanded = expandedGroups.has(theaterName)
        
        return (
          <div key={theaterName} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            {/* Theater Header */}
            <button
              className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition flex items-center justify-between text-left"
              onClick={() => toggleGroup(theaterName)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">{theaterName}</h3>
                {theaterData.distance !== undefined && (
                  <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full shrink-0">
                    <MapPin size={10} />
                    {formatDistance(theaterData.distance)}
                  </span>
                )}
              </div>
              <ChevronDown
                size={20}
                className={`text-slate-500 transition-transform shrink-0 ml-2 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Theater Content */}
            {isExpanded && (
              <div className="p-4 space-y-4">
                {Object.entries(theaterData.formats)
                  .sort(([formatA], [formatB]) => {
                    const priorityA = getFormatPriority(formatA)
                    const priorityB = getFormatPriority(formatB)
                    if (priorityA !== priorityB) {
                      return priorityA - priorityB
                    }
                    return formatA.localeCompare(formatB)
                  })
                  .map(([format, dateGroups]) => (
                    <div key={`${theaterName}-${format}`}>
                    {/* Format Badge */}
                    <div className="mb-2">
                      <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {format}
                      </span>
                    </div>

                    {/* Dates and Showtimes */}
                    <div className="space-y-3">
                      {(() => {
                        const dateEntries = Object.entries(dateGroups).sort(([a], [b]) => a.localeCompare(b))
                        const key = `${theaterName}-${format}`
                        const isExpanded = expandedDates[key]
                        
                        const shouldTruncate = dateEntries.length > 5 && !isExpanded
                        const datesToShow = shouldTruncate 
                          ? [dateEntries[0], dateEntries[1], dateEntries[2], dateEntries[dateEntries.length - 1]] 
                          : dateEntries

                        return (
                          <>
                            {datesToShow.map(([date, dateShowtimes], idx) => {
                              const isLastItemAndTruncated = shouldTruncate && idx === 3
                              return (
                                <div key={`${theaterName}-${format}-${date}`}>
                                  {isLastItemAndTruncated && (
                                    <button
                                      type="button"
                                      onClick={() => toggleDatesExpand(key)}
                                      className="w-full text-center py-1.5 my-2 border border-dashed border-slate-200 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded transition"
                                    >
                                      + Show {dateEntries.length - 4} more days
                                    </button>
                                  )}
                                  
                                  {/* Date Label */}
                                  <div className="text-xs font-medium text-slate-600 mb-1.5 flex items-center justify-between">
                                    <span>{formatDateLabel(date)}</span>
                                    {isLastItemAndTruncated && (
                                      <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-semibold">
                                        Last Showing
                                      </span>
                                    )}
                                  </div>

                                  {/* Compact Showtime Chips */}
                                  <div className="flex flex-wrap gap-1.5">
                                    {dateShowtimes
                                      .sort((a, b) => 
                                        new Date(a.showDateTimeLocal).getTime() - 
                                        new Date(b.showDateTimeLocal).getTime()
                                      )
                                      .map((showtime, idx) => {
                                        const isSoldOut = showtime.isSoldOut
                                        const isCanceled = showtime.isCanceled
                                        const isPast = showtime.showDateTimeUtc ? new Date(showtime.showDateTimeUtc) < new Date() : false
                                        const parsed = parseShowtimeAttributes(showtime.attributes)
                                        const langLabel = parsed.language

                                        if (isCanceled) return null
                                        
                                        return (
                                          <a
                                            key={`${showtime.showId}-${idx}`}
                                            href={(isSoldOut || isPast) ? undefined : showtime.purchaseUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={e => (isSoldOut || isPast) && e.preventDefault()}
                                            className={`
                                              inline-flex items-center px-2.5 py-1 rounded text-sm font-medium transition
                                              ${isSoldOut 
                                                ? 'bg-slate-100 text-slate-400 line-through cursor-not-allowed' 
                                                : isPast
                                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-60'
                                                  : 'bg-slate-900 text-white hover:bg-slate-700 cursor-pointer'
                                              }
                                            `}
                                            title={isSoldOut ? 'Sold Out' : isPast ? 'Showtime Passed' : `Buy tickets for ${formatTime(showtime.showDateTimeLocal)}${langLabel ? ` (${langLabel})` : ''}`}
                                          >
                                            {formatTime(showtime.showDateTimeLocal)}
                                          </a>
                                        )
                                      })}
                                  </div>
                                </div>
                              )
                            })}
                            
                            {isExpanded && dateEntries.length > 5 && (
                              <button
                                type="button"
                                onClick={() => toggleDatesExpand(key)}
                                className="w-full text-center py-1.5 mt-2 border border-dashed border-slate-200 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded transition"
                              >
                                - Collapse dates
                              </button>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {mergedShowtimes.length === 0 && (
        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
          <p>No showtimes available for current filters.</p>
        </div>
      )}
    </div>
  )
}
